/**
 * Shared Full Exam / Trial runtime exit authority.
 * Synchronous abort flag prevents post-exit startup continuations.
 *
 * Explicit Quit / browser-back uses SILENT_ABORT_ON_EXPLICIT_EXIT:
 * markLiveExamRuntimeExiting() is the signal; tracked HTMLAudio / BufferSource
 * / pending audio callbacks must not remain audible after route exit.
 * Normal task completion must NOT call markLiveExamRuntimeExiting().
 */

let runtimeExiting = false;
let audioEpoch = 0;

const trackedHtmlAudio = new Set<HTMLAudioElement>();
const trackedBufferSources = new Set<AudioBufferSourceNode>();
const trackedCallbackTimers = new Set<number>();

export function markLiveExamRuntimeExiting(): void {
  runtimeExiting = true;
  abortLiveExamRuntimeAudio();
}

export function isLiveExamRuntimeExiting(): boolean {
  return runtimeExiting;
}

export function getLiveExamAudioEpoch(): number {
  return audioEpoch;
}

export function shouldStartLiveExamAudio(): boolean {
  return !runtimeExiting;
}

export function resetLiveExamRuntimeExitState(): void {
  abortLiveExamRuntimeAudio();
  runtimeExiting = false;
}

/** Canonical entry for a valid live-exam task segment — not an abandon. */
export function beginLiveExamRuntimeSegment(): void {
  resetLiveExamRuntimeExitState();
}

function stopHtmlAudioElement(audio: HTMLAudioElement): void {
  try {
    audio.pause();
  } catch {
    /* noop */
  }
  try {
    audio.muted = true;
    audio.currentTime = 0;
  } catch {
    /* noop */
  }
  try {
    audio.removeAttribute('src');
    audio.src = '';
    audio.srcObject = null;
    audio.load();
  } catch {
    /* noop */
  }
}

function stopBufferSource(source: AudioBufferSourceNode): void {
  try {
    source.onended = null;
    source.stop();
  } catch {
    /* noop */
  }
  try {
    source.disconnect();
  } catch {
    /* noop */
  }
}

/**
 * Stop locally-created audio objects that DOM teardown cannot reach
 * (`new Audio(url)` is not in the document; BufferSources are not HTML).
 * Does not close AudioContext so a later normal ending clip can still play
 * when this is used from timer/finish stopAllAudio — not from explicit quit.
 */
/** Abort tracked playback + pending callbacks. Explicit Quit only — not timer/tool completion. */
export function stopTrackedLiveExamAudio(): void {
  audioEpoch += 1;

  trackedCallbackTimers.forEach((id) => {
    try {
      window.clearTimeout(id);
    } catch {
      /* noop */
    }
  });
  trackedCallbackTimers.clear();

  trackedBufferSources.forEach((source) => stopBufferSource(source));
  trackedBufferSources.clear();

  trackedHtmlAudio.forEach((audio) => stopHtmlAudioElement(audio));
  trackedHtmlAudio.clear();
}

/** Explicit-exit audio abort: tracked objects + any DOM <audio> nodes. */
export function abortLiveExamRuntimeAudio(): void {
  stopTrackedLiveExamAudio();
  stopDomAudioElements();
}

export function trackLiveExamHtmlAudio(audio: HTMLAudioElement): boolean {
  if (runtimeExiting) {
    stopHtmlAudioElement(audio);
    return false;
  }
  trackedHtmlAudio.add(audio);
  return true;
}

export function untrackLiveExamHtmlAudio(audio: HTMLAudioElement): void {
  trackedHtmlAudio.delete(audio);
}

export function trackLiveExamBufferSource(source: AudioBufferSourceNode): boolean {
  if (runtimeExiting) {
    stopBufferSource(source);
    return false;
  }
  trackedBufferSources.add(source);
  return true;
}

export function untrackLiveExamBufferSource(source: AudioBufferSourceNode): void {
  trackedBufferSources.delete(source);
}

export function trackLiveExamCallbackTimer(id: number): number {
  if (runtimeExiting) {
    try {
      window.clearTimeout(id);
    } catch {
      /* noop */
    }
    return id;
  }
  trackedCallbackTimers.add(id);
  return id;
}

export function untrackLiveExamCallbackTimer(id: number): void {
  trackedCallbackTimers.delete(id);
}

export function invokeUnlessLiveExamExiting(callback?: () => void, epoch?: number): void {
  if (!callback) return;
  if (runtimeExiting) return;
  if (typeof epoch === 'number' && epoch !== audioEpoch) return;
  callback();
}

/** Timeout that cannot run audio/navigation work after explicit quit. */
export function scheduleLiveExamCallback(callback: () => void, delayMs: number): number {
  const epoch = audioEpoch;
  const id = window.setTimeout(() => {
    untrackLiveExamCallbackTimer(id);
    invokeUnlessLiveExamExiting(callback, epoch);
  }, delayMs);
  return trackLiveExamCallbackTimer(id);
}

export function stopDomAudioElements(): void {
  try {
    document.querySelectorAll('audio').forEach((audio) => {
      stopHtmlAudioElement(audio);
    });
  } catch {
    /* noop */
  }
}

export function closeGlobalAudioContext(): void {
  try {
    const win = window as Window & { __appAudioContext?: AudioContext };
    const ctx = win.__appAudioContext;
    if (ctx && ctx.state !== 'closed') {
      void ctx.close();
    }
    delete win.__appAudioContext;
  } catch {
    /* noop */
  }
}

type GlobalAgentRef = {
  stopAllAudio?: () => void;
  stop?: () => void;
  cleanupGlobalAudio?: () => void;
  setMicEnabled?: (enabled: boolean) => void;
};

type GlobalAudioServiceRef = {
  cleanup?: () => void;
  cleanupGlobalAudio?: () => void;
  stopRecording?: () => void;
  requestShutdown?: (reason: string) => void;
};

/** Best-effort shutdown of window-scoped runtime services (VoiceAgent, AudioService). */
export function stopGlobalSpeakingRuntimeServices(): void {
  try {
    const win = window as Window & {
      agentRef?: { current?: GlobalAgentRef };
      audioServiceRef?: { current?: GlobalAudioServiceRef };
    };

    const agent = win.agentRef?.current;
    if (agent) {
      agent.setMicEnabled?.(false);
      agent.stopAllAudio?.();
      agent.stop?.();
      agent.cleanupGlobalAudio?.();
    }
    delete win.agentRef;

    const audioService = win.audioServiceRef?.current;
    if (audioService) {
      audioService.stopRecording?.();
      audioService.cleanup?.();
      audioService.cleanupGlobalAudio?.();
    }
  } catch {
    /* noop */
  }
}

export function runLiveExamRuntimeTeardown(): void {
  markLiveExamRuntimeExiting();
  stopGlobalSpeakingRuntimeServices();
  stopDomAudioElements();
  closeGlobalAudioContext();
}
